import * as React from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { DriveTable } from "./workspace/DriveTable";
import { DriveTableRow } from "./workspace/DriveTableRow";
import { DragOverlayContent } from "./components/DragOverlayContent";
import HomeRow from "./homeRow";
import { type UploadStatusMessage, useUploadStatus } from "@/hooks/use-upload-status";
import { FolderItem, Item } from "@/types/fse";
import { TxType } from "@/types/transaction";
import { ItemTxProgress } from "@/views/drive/types";
import { advancePhaseEvent } from "@/views/drive/modals/uploadModal/phaseLabels";
import { isDriveProgressEvent } from "./driveProgressEvents";
import type { DirectoryUsersById } from "./workspace/types";
import type { ClassStyle } from "./documentMap/types";

const DRIVE_PROGRESS_TX_TYPES = [TxType.UPLOAD, TxType.CONTRACT_FAMILY_GROUPING];

interface Props {
  items: Item[];
  emptyState?: React.ReactNode;
  usersById: DirectoryUsersById;
  authenticatedUserId: string;
  parentFolder: FolderItem | null;
  isRootFolder: boolean;
  currentPage: number;
  itemsPerPage: number;
  currentFolderId: string | null;
  hiddenButtons?: string[];
  onMoveItem?: (sourceItem: Item, targetFolder: FolderItem) => void;
  movingItemIds?: Set<string>;
  selectedItemIds?: Set<string>;
  onItemSelection?: (itemId: string, isCtrlOrCmd: boolean, shiftKey?: boolean, targetChecked?: boolean) => void;
  onClearSelection?: () => void;
  onToggleSelect?: (itemId: string, checked: boolean, shiftKey: boolean) => void;
  onToggleSelectAll?: () => void;
  canMutateItem?: (item: Item) => boolean;
  /** Split view: the Role of a row; a folder has none. Absent: the ordinary columns. */
  splitRoleFor?: (item: Item) => ClassStyle | null;
}

export const DriveList: React.FC<Props> = ({
  items,
  emptyState,
  usersById,
  authenticatedUserId,
  parentFolder,
  isRootFolder,
  hiddenButtons = [],
  onMoveItem,
  movingItemIds = new Set(),
  selectedItemIds = new Set(),
  currentFolderId,
  onItemSelection,
  onClearSelection,
  onToggleSelectAll,
  canMutateItem = () => true,
  splitRoleFor,
}) => {
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const sensors = useSensors(pointerSensor);

  const [progressForItem, setProgressForItem] = React.useState<Record<string, ItemTxProgress>>({});
  const [activeItem, setActiveItem] = React.useState<Item | null>(null);
  const [isDraggedItemSelected, setIsDraggedItemSelected] = React.useState(false);

  const onUploadStatusChange = React.useCallback((event: UploadStatusMessage) => {
    if (isDriveProgressEvent(event) && DRIVE_PROGRESS_TX_TYPES.includes(event.tx_type)) {
      setProgressForItem(prev => {
        const next = { ...prev };
        event.fse_ids.forEach(fseId => {
          next[fseId] = {
            progress: event.progress,
            txType: event.tx_type,
            status: event.status,
            errorCode: event.error_code || undefined,
            currentEvent: advancePhaseEvent(prev[fseId]?.currentEvent, event.current_event),
          };
        });
        return next;
      });
    }
  }, []);

  useUploadStatus(onUploadStatusChange);

  const handleDragStart = (event: DragStartEvent) => {
    const draggedItem = event.active.data.current?.item as Item;
    const isSelected = event.active.data.current?.isSelected as boolean;

    if (draggedItem) {
      setActiveItem(draggedItem);
      setIsDraggedItemSelected(isSelected);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveItem(null);
    setIsDraggedItemSelected(false);
    const { active, over } = event;

    if (!over || !onMoveItem) return;

    const draggedItem = active.data.current?.item as Item;

    if (!draggedItem) return;

    // Check if dropping on HomeRow (parent directory)
    const isHomeRowTarget = over.data.current?.isHomeRow;
    if (isHomeRowTarget) {
      const targetParentFolder = over.data.current?.parentFolder as FolderItem;
      if (targetParentFolder) {
        if (draggedItem.parent_fs_id === targetParentFolder.id) {
          return;
        }
        onMoveItem(draggedItem, targetParentFolder);
      }
      return;
    }

    // Handle regular folder drops
    const targetItem = over.data.current?.item as Item;
    if (!targetItem) return;

    // Prevent dropping on itself or if target is not a folder
    if (draggedItem.id === targetItem.id || targetItem.type !== "folder") {
      return;
    }

    onMoveItem(draggedItem, targetItem as FolderItem);
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    // Clear selection when clicking on empty space
    if (e.target === e.currentTarget && onClearSelection) {
      onClearSelection();
    }
  };

  const handleToggleSelect = React.useCallback(
    (itemId: string, checked: boolean, shiftKey: boolean) => {
      if (!onItemSelection) return;

      onItemSelection(itemId, true, shiftKey, checked);
    },
    [onItemSelection],
  );

  const mutableItemIds = items.filter(canMutateItem).map(item => item.id);
  const selectedMutableCount = mutableItemIds.filter(id => selectedItemIds.has(id)).length;
  const isAllSelected = mutableItemIds.length > 0 && selectedMutableCount === mutableItemIds.length;
  const isPartiallySelected = selectedMutableCount > 0 && selectedMutableCount < mutableItemIds.length;

  const additionalCount = isDraggedItemSelected ? selectedItemIds.size - 1 : 0;

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div onClick={handleContainerClick}>
        {!isRootFolder && parentFolder ? <HomeRow parentFolder={parentFolder} /> : null}
        <DriveTable
          isAllSelected={isAllSelected}
          isPartiallySelected={isPartiallySelected}
          onToggleSelectAll={mutableItemIds.length > 0 ? onToggleSelectAll : undefined}
          split={splitRoleFor !== undefined}
        >
          {items.length === 0
            ? emptyState
            : items.map(item => (
                <DriveTableRow
                  onToggleSelect={handleToggleSelect}
                  key={item.id}
                  item={item}
                  usersById={usersById}
                  authenticatedUserId={authenticatedUserId}
                  progressForItem={progressForItem}
                  hiddenButtons={hiddenButtons}
                  isMoving={movingItemIds.has(item.id)}
                  isSelected={selectedItemIds.has(item.id)}
                  onItemSelection={onItemSelection}
                  currentFolderId={currentFolderId}
                  isMutable={canMutateItem(item)}
                  splitRole={splitRoleFor?.(item)}
                />
              ))}
        </DriveTable>
      </div>
      <DragOverlay>
        {activeItem ? <DragOverlayContent item={activeItem} additionalCount={additionalCount} /> : null}
      </DragOverlay>
    </DndContext>
  );
};
